import { Schema } from "effect";
import { NonNegativeInt, PositiveInt, type Instant } from "../../srs/types.ts";
import { homophone } from "../model/homophones.ts";
import { toneMarks } from "../model/pinyin.ts";
import { reviewQueue } from "../model/queue.ts";
import { Card, CardId, Rev, type Flashcard, type FlashcardSet, type Kind, type Prerequisite } from "../model/types.ts";

export const SessionCard = Schema.Struct({
  ...Card.fields,
  level: PositiveInt,
  id: CardId,
  reps: NonNegativeInt,
  state: Schema.Literals(["new", "learning", "review", "relearning"]),
  contentRev: Rev,
  settingsRev: Rev,
});
export type SessionCard = typeof SessionCard.Type;

export function kindOf(set: FlashcardSet, kind: string | null): Kind {
  if (kind === "pinyin" || kind === "recall") return kind;
  if (kind !== null && set.settings.kinds[kind]?.input === "pinyin") return "pinyin";
  return "meaning";
}

const POS: Record<string, string> = {
  N: "noun",
  V: "verb",
  Adj: "adjective",
  Adv: "adverb",
  M: "measure word",
  Pron: "pronoun",
  Conj: "conjunction",
  Prep: "preposition",
  Num: "numeral",
  Aux: "auxiliary",
  Intj: "interjection",
  Part: "particle",
  Prefix: "prefix",
  Suffix: "suffix",
  Phonetic: "onomatopoeia",
};

export function posText(pos: string): string {
  const names = [...new Set(pos.split("/").map((p) => p.trim()).filter(Boolean).map((p) => POS[p] ?? p))];
  return names.length > 1 ? `${names.slice(0, -1).join(", ")} or ${names.at(-1)}` : (names[0] ?? "");
}

export function toCard(set: FlashcardSet, card: Flashcard): SessionCard {
  const kind = kindOf(set, card.kind);
  const { fields } = card;
  const pos = typeof fields.pos === "string" ? posText(fields.pos) : "";
  const pinyin = kind === "meaning" && typeof fields.pinyin === "string" ? toneMarks(fields.pinyin.trim()) : "";
  const spoken = kind === "pinyin" ? card.prompt : fields.word;
  const say = kind !== "meaning" && typeof spoken === "string" ? homophone(spoken, card.answer) : undefined;
  return {
    id: card.id,
    reps: NonNegativeInt.make(card.state.tag === "new" ? 0 : card.state.memory.reps),
    state: card.state.tag,
    contentRev: card.contentRev,
    settingsRev: set.settingsRev,
    prompt: card.prompt,
    kind,
    answer: kind === "meaning" ? card.answer : toneMarks(card.answer),
    accepted: [...card.accepted],
    set: set.name,
    level: card.level,
    ...(typeof fields.word === "string" && fields.word ? { word: fields.word } : {}),
    ...(pos ? { pos } : {}),
    ...(pinyin ? { pinyin } : {}),
    ...(say ? { say } : {}),
  };
}

export function sessionQueue(
  set: FlashcardSet,
  cards: readonly Flashcard[],
  now: Instant,
  prerequisites: readonly Prerequisite[] = [],
): SessionCard[] {
  return reviewQueue(set, cards, now, prerequisites).map((card) => toCard(set, card));
}
